import React, { useEffect, useState } from "react"

import { AlertCircle, Check, Edit } from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/contexts/AuthContext"
import { useUpdatePerson } from "@/hooks/useUpdatePerson"
import { Person } from "@/types/person"

import type { Enums, TablesUpdate } from "../../../../database.types"

interface FinanceDetailsTabProps {
  person: Person
}

const FinanceDetailsTab: React.FC<FinanceDetailsTabProps> = ({ person }) => {
  const [personData, setPersonData] = useState(person)
  const [openDialog, setOpenDialog] = useState<string | null>(null)
  const [formState, setFormState] = useState({ ...person })
  const { updatePerson, isUpdating, updateError } = useUpdatePerson()
  const { claims } = useAuth()

  const teamPermissions = claims.team_permissions || []
  const canEditFinance = teamPermissions.includes("update.tab.finance")

  useEffect(() => {
    setPersonData(person)
  }, [person])

  const handleOpenDialog = (dialogName: string) => {
    setFormState({ ...personData })
    setOpenDialog(dialogName)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canEditFinance) return

    try {
      const fields: TablesUpdate<"people"> = {}

      if (openDialog === "bank") {
        if (formState.bank_name !== personData.bank_name) {
          fields.bank_name = formState.bank_name
        }
        if (formState.bank_account_number !== personData.bank_account_number) {
          fields.bank_account_number = formState.bank_account_number
        }
        if (formState.bank_sort_code !== personData.bank_sort_code) {
          fields.bank_sort_code = formState.bank_sort_code
        }
        if (formState.name_on_bank_account !== personData.name_on_bank_account) {
          fields.name_on_bank_account = formState.name_on_bank_account
        }
      } else if (openDialog === "payment") {
        if (formState.payment_basis !== personData.payment_basis) {
          fields.payment_basis = formState.payment_basis
        }
        if (formState.pay_basis !== personData.pay_basis) {
          fields.pay_basis = formState.pay_basis
        }
        if (formState.pay_group !== personData.pay_group) {
          fields.pay_group = formState.pay_group
        }
        if (formState.pay_rate !== personData.pay_rate) {
          fields.pay_rate = formState.pay_rate
        }
        if (formState.salary !== personData.salary) {
          fields.salary = formState.salary
        }
        if (formState.salaried_hours !== personData.salaried_hours) {
          fields.salaried_hours = formState.salaried_hours
        }
      } else if (openDialog === "personal-company") {
        if (formState.unique_taxpayer_reference !== personData.unique_taxpayer_reference) {
          fields.unique_taxpayer_reference = formState.unique_taxpayer_reference
        }
        if (formState.company_name !== personData.company_name) {
          fields.company_name = formState.company_name
        }
        if (formState.company_number !== personData.company_number) {
          fields.company_number = formState.company_number
        }
        if (formState.vat_number !== personData.vat_number) {
          fields.vat_number = formState.vat_number
        }
        if (formState.national_insurance_number !== personData.national_insurance_number) {
          fields.national_insurance_number = formState.national_insurance_number
        }
        if (formState.student_loan_plan_1 !== personData.student_loan_plan_1) {
          fields.student_loan_plan_1 = formState.student_loan_plan_1
        }
        if (formState.student_loan_plan_2 !== personData.student_loan_plan_2) {
          fields.student_loan_plan_2 = formState.student_loan_plan_2
        }
        if (formState.student_loan_plan_4 !== personData.student_loan_plan_4) {
          fields.student_loan_plan_4 = formState.student_loan_plan_4
        }
        if (formState.student_loan_graduate_plan !== personData.student_loan_graduate_plan) {
          fields.student_loan_graduate_plan = formState.student_loan_graduate_plan
        }
      }

      if (Object.keys(fields).length > 0) {
        const response = await updatePerson({
          id: personData.id,
          fields,
        })
        const updatedPerson = { ...personData, ...response }
        setFormState(updatedPerson)
        setPersonData(updatedPerson)
        setOpenDialog(null)
      }
    } catch (error) {
      console.error("Error updating details:", error)
    }
  }

  return (
    <div className="space-y-6">
      {updateError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {updateError instanceof Error ? updateError.message : "Failed to update"}
          </AlertDescription>
        </Alert>
      )}

      {/* Bank Details */}
      <Dialog
        open={openDialog === "bank"}
        onOpenChange={(v) => (v ? handleOpenDialog("bank") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Bank Details</CardTitle>
            {canEditFinance && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Bank Name</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.bank_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Account Number
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.bank_account_number || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Sort Code</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.bank_sort_code || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Name on Account
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.name_on_bank_account || "Not provided"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Bank Details</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Bank Name</label>
                <Input
                  value={formState.bank_name || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      bank_name: e.target.value,
                    })
                  }
                  placeholder="Enter bank name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Account Number
                </label>
                <Input
                  value={formState.bank_account_number || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      bank_account_number: e.target.value,
                    })
                  }
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Sort Code</label>
                <Input
                  value={formState.bank_sort_code || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      bank_sort_code: e.target.value,
                    })
                  }
                  placeholder="Enter sort code"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Name on Account
                </label>
                <Input
                  value={formState.name_on_bank_account || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      name_on_bank_account: e.target.value,
                    })
                  }
                  placeholder="Enter name on account"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Details */}
      <Dialog
        open={openDialog === "payment"}
        onOpenChange={(v) => (v ? handleOpenDialog("payment") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Payment Details</CardTitle>
            {canEditFinance && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Payment Basis
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.payment_basis || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Basis</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.pay_basis || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Group</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.pay_group || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Rate</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  £{personData.pay_rate || "0"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Salary</label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  £{personData.salary || "0"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Salaried Hours
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.salaried_hours || "0"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment Details</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Payment Basis
                </label>
                <Select
                  value={formState.payment_basis || ""}
                  onValueChange={(value) =>
                    setFormState({ ...formState, payment_basis: value as Enums<"payment_basis"> })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment basis" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="Route-based">Route-based</SelectItem>
                    <SelectItem value="Hourly-based">Hourly-based</SelectItem>
                    <SelectItem value="Salary">Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Basis</label>
                <Select
                  value={formState.pay_basis || ""}
                  onValueChange={(value) =>
                    setFormState({ ...formState, pay_basis: value as Enums<"pay_basis"> })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pay basis" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="Hourly">Hourly</SelectItem>
                    <SelectItem value="Daily">Daily</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Group</label>
                <Select
                  value={formState.pay_group || ""}
                  onValueChange={(value) =>
                    setFormState({ ...formState, pay_group: value as Enums<"pay_group"> })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pay group" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="Fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Pay Rate</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formState.pay_rate || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      pay_rate: parseFloat(e.target.value),
                    })
                  }
                  placeholder="Enter pay rate"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">Salary</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formState.salary || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      salary: parseFloat(e.target.value),
                    })
                  }
                  placeholder="Enter salary"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Salaried Hours
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formState.salaried_hours || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      salaried_hours: parseFloat(e.target.value),
                    })
                  }
                  placeholder="Enter salaried hours"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Personal & Company Details */}
      <Dialog
        open={openDialog === "personal-company"}
        onOpenChange={(v) => (v ? handleOpenDialog("personal-company") : setOpenDialog(null))}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Personal & Company Details</CardTitle>
            {canEditFinance && (
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Unique Taxpayer Reference
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.unique_taxpayer_reference || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Company Name
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.company_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Company Number
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.company_number || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  VAT Number
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.vat_number || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  National Insurance Number
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.national_insurance_number || "Not provided"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 1
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.student_loan_plan_1 ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 2
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.student_loan_plan_2 ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 4
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.student_loan_plan_4 ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Postgraduate Loan
                </label>
                <p className="mt-1 text-sm text-palette-jungleGreen900">
                  {personData.student_loan_graduate_plan ? "Yes" : "No"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Personal & Company Details</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Unique Taxpayer Reference
                </label>
                <Input
                  type="number"
                  value={formState.unique_taxpayer_reference ?? ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      unique_taxpayer_reference: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  placeholder="Enter UTR"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Company Name
                </label>
                <Input
                  value={formState.company_name || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      company_name: e.target.value,
                    })
                  }
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Company Number
                </label>
                <Input
                  value={formState.company_number || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      company_number: e.target.value,
                    })
                  }
                  placeholder="Enter company number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  VAT Number
                </label>
                <Input
                  value={formState.vat_number || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      vat_number: e.target.value,
                    })
                  }
                  placeholder="Enter VAT number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  National Insurance Number
                </label>
                <Input
                  value={formState.national_insurance_number || ""}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      national_insurance_number: e.target.value,
                    })
                  }
                  placeholder="Enter NI number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 1
                </label>
                <Select
                  value={formState.student_loan_plan_1 ? "yes" : "no"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      student_loan_plan_1: value === "yes" ? "Yes" : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 2
                </label>
                <Select
                  value={formState.student_loan_plan_2 ? "yes" : "no"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      student_loan_plan_2: value === "yes" ? "Yes" : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Student Loan 4
                </label>
                <Select
                  value={formState.student_loan_plan_4 ? "yes" : "no"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      student_loan_plan_4: value === "yes" ? "Yes" : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-palette-jungleGreen700">
                  Postgraduate Loan
                </label>
                <Select
                  value={formState.student_loan_graduate_plan ? "yes" : "no"}
                  onValueChange={(value) =>
                    setFormState({
                      ...formState,
                      student_loan_graduate_plan: value === "yes" ? "Yes" : null,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default FinanceDetailsTab
